class FileStorageService {
  dbName: string = 'FileStorage_v1';
  version: number = 1;
  db!: IDBDatabase;

  private ready!: () => void;
  private readyPromise = new Promise<void>(resolve => (this.ready = resolve));

  init() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;

        this.ready();

        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('files')) {
          const store = db.createObjectStore('files', { keyPath: 'id' });

          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async store(id: string, file: File) {
    await this.readyPromise;

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readwrite');
      const store = transaction.objectStore('files');

      const imageData: FileStorage.Entity = {
        id: id,
        timestamp: Date.now(),
        mime: file.type,

        file: file,
      };

      const request = store.add(imageData);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getOne(id: string) {
    await this.readyPromise;

    return new Promise<FileStorage.Entity | null>((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readonly');
      const store = transaction.objectStore('files');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  async getBlobUrl(id: string) {
    return this.getOne(id).then(result => {
      if (result) {
        return URL.createObjectURL(result.file);
      }

      return null;
    });
  }

  async getAll() {
    await this.readyPromise;

    return new Promise<FileStorage.Entity[]>((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readonly');
      const store = transaction.objectStore('files');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(id: string) {
    await this.readyPromise;

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db.transaction(['files'], 'readwrite');
      const store = transaction.objectStore('files');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const FileStorage = new FileStorageService();

export namespace FileStorage {
  export type Entity = {
    id: string;
    timestamp: number;
    mime: string;

    file: File;
  };
}

FileStorage.init();
